import { useState } from 'react';
import { Plus, Trash2, GripVertical, Pencil, ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, ChevronsDownUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { QuestionEditor } from './QuestionEditor';
import type { TemplateSection } from '@/api/hooks/useAttestations';
import type { QuestionUpdatePatch } from '@/api/hooks/useTemplateEdit';

interface SectionHandlers {
  onDeleteSection: (sectionId: string) => void;
  onUpdateSection: (sectionId: string, name: string, description: string | null) => void;
  onUpdateSectionWeight: (sectionId: string, weight: number) => void;
  onAddQuestion: (sectionId: string, text: string, responseType: string, maxScore: number, isRequired: boolean) => void;
  onDeleteQuestion: (questionId: string) => void;
  onUpdateQuestion: (questionId: string, patch: QuestionUpdatePatch) => void;
  onReorderQuestions: (sectionId: string, orderedIds: string[]) => void;
  onUpdateBypassEligibility?: (questionId: string, certTypes: string[]) => void;
  allowBypass?: boolean;
}

interface Props extends SectionHandlers {
  sections: TemplateSection[];
  onAddSection: (name: string, description?: string) => void;
  onReorderSections: (orderedIds: string[]) => void;
}

// One sortable section card. Drag handle → reorder (mouse + keyboard); move-up/down buttons are the
// robust a11y fallback. Collapsed by default — the header stays visible so it can be dragged while
// collapsed. All S1–S3 inline controls (weight input, edit, delete, questions) are preserved.
function SortableSection({
  section, isFirst, isLast, expanded, onToggle, onMoveSection, h,
}: {
  section: TemplateSection;
  isFirst: boolean;
  isLast: boolean;
  expanded: boolean;
  onToggle: () => void;
  onMoveSection: (sectionId: string, dir: -1 | 1) => void;
  h: SectionHandlers;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(section.name);
  const [editDesc, setEditDesc] = useState(section.description ?? '');

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
    position: isDragging ? 'relative' : undefined,
  };

  function startEdit() { setEditName(section.name); setEditDesc(section.description ?? ''); setEditing(true); }
  function save() { if (!editName.trim()) return; h.onUpdateSection(section.id, editName.trim(), editDesc.trim() || null); setEditing(false); }

  return (
    <div ref={setNodeRef} style={style} className="border border-border-subtle rounded-md overflow-hidden bg-bg-surface">
      {editing ? (
        <div className="px-4 py-3 space-y-2">
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Section name" className="text-sm font-medium" autoFocus />
          <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Description (optional)" className="text-sm" />
          <div className="flex gap-2">
            <Button size="sm" variant="default" onClick={save}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" variant="ghost" className="ml-auto text-destructive hover:text-destructive" onClick={() => { setEditing(false); h.onDeleteSection(section.id); }}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete section
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between px-3 py-3 group">
          <div className="flex items-center gap-1.5 min-w-0">
            {/* Drag handle — a focusable NON-button element (attributes supply role/tabIndex) so the
                keyboard sensor works without a native button's Space→click cancelling the drag. */}
            <span
              ref={setActivatorNodeRef}
              className="shrink-0 cursor-grab touch-none rounded p-0.5 text-text-muted hover:text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-cyan active:cursor-grabbing"
              aria-label={`Drag to reorder section ${section.name} (or focus and press Space, then arrow keys)`}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </span>
            <button type="button" onClick={onToggle} className="shrink-0 rounded p-0.5 text-text-secondary hover:text-text-primary" aria-label={`${expanded ? 'Collapse' : 'Expand'} section ${section.name}`} aria-expanded={expanded}>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            <span className="text-sm font-medium text-text-primary truncate">{section.name}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Move up/down — robust keyboard/a11y reorder fallback (works regardless of drag-keyboard). */}
            <div className="flex flex-col">
              <button type="button" onClick={() => onMoveSection(section.id, -1)} disabled={isFirst} aria-label={`Move section ${section.name} up`} className="rounded p-0.5 text-text-muted hover:text-text-primary disabled:opacity-30"><ChevronUp className="h-3 w-3" /></button>
              <button type="button" onClick={() => onMoveSection(section.id, 1)} disabled={isLast} aria-label={`Move section ${section.name} down`} className="rounded p-0.5 text-text-muted hover:text-text-primary disabled:opacity-30"><ChevronDown className="h-3 w-3" /></button>
            </div>
            <span className="flex items-center gap-1 rounded border border-border-subtle bg-bg-canvas px-1.5 py-0.5" title="Section weight (%)">
              <input
                type="number" min={0} max={100} step={1}
                defaultValue={section.weight_pct}
                key={`weight-${section.id}-${section.weight_pct}`}
                aria-label={`Weight for ${section.name}`}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isInteger(n) && n >= 0 && n <= 100) {
                    if (n !== section.weight_pct) h.onUpdateSectionWeight(section.id, n);
                  } else {
                    e.target.value = String(section.weight_pct);
                    toast.error('Section weight must be a whole number between 0 and 100.');
                  }
                }}
                className="w-9 bg-transparent text-[11px] text-text-primary focus:outline-none"
              />
              <span className="text-[10px] text-text-muted">%</span>
            </span>
            <Badge variant="muted" className="text-[10px]">{section.questions.length} questions</Badge>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-text-muted opacity-0 group-hover:opacity-100" onClick={startEdit} aria-label={`Edit section ${section.name}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {!editing && expanded && (
        <QuestionEditor
          section={section}
          noSectionHeader
          onAddQuestion={h.onAddQuestion}
          onDeleteQuestion={h.onDeleteQuestion}
          onUpdateQuestion={h.onUpdateQuestion}
          onReorderQuestions={h.onReorderQuestions}
          onUpdateBypassEligibility={h.onUpdateBypassEligibility}
          allowBypass={h.allowBypass}
        />
      )}
    </div>
  );
}

export function SectionEditor({ sections, onAddSection, onReorderSections, ...handlers }: Props) {
  const [addingSection, setAddingSection] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  // Collapsed by default — a Set of the currently-EXPANDED section ids (empty = all collapsed).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleAddSection() {
    if (!newName.trim()) return;
    onAddSection(newName.trim(), newDesc.trim() || undefined);
    setNewName(''); setNewDesc(''); setAddingSection(false);
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldI = sections.findIndex((s) => s.id === active.id);
    const newI = sections.findIndex((s) => s.id === over.id);
    if (oldI < 0 || newI < 0) return;
    onReorderSections(arrayMove(sections, oldI, newI).map((s) => s.id));
  }

  function moveSection(id: string, dir: -1 | 1) {
    const i = sections.findIndex((s) => s.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= sections.length) return;
    onReorderSections(arrayMove(sections, i, j).map((s) => s.id));
  }

  function toggle(id: string) {
    setExpandedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  const allExpanded = sections.length > 0 && sections.every((s) => expandedIds.has(s.id));

  return (
    <div className="space-y-3">
      {sections.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setExpandedIds(allExpanded ? new Set() : new Set(sections.map((s) => s.id)))}
            className="flex items-center gap-1.5 rounded-md border border-border-subtle bg-bg-surface px-2.5 py-1 text-xs text-text-secondary hover:text-text-primary"
          >
            {allExpanded ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />}
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      )}

      {sections.length === 0 && !addingSection && (
        <p className="text-sm text-text-muted text-center py-8">
          No sections yet. Add a section to start building your template.
        </p>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {sections.map((section, i) => (
              <SortableSection
                key={section.id}
                section={section}
                isFirst={i === 0}
                isLast={i === sections.length - 1}
                expanded={expandedIds.has(section.id)}
                onToggle={() => toggle(section.id)}
                onMoveSection={moveSection}
                h={handlers}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {addingSection ? (
        <div className="border border-border-subtle rounded-md px-4 py-3 bg-bg-surface space-y-2">
          <p className="text-xs font-medium text-text-secondary">New Section</p>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Section name *" className="text-sm" autoFocus />
          <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optional)" className="text-sm" />
          <div className="flex gap-2">
            <Button size="sm" variant="default" onClick={handleAddSection} disabled={!newName.trim()}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAddingSection(false); setNewName(''); setNewDesc(''); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" className="w-full text-xs text-text-secondary h-9 border-dashed" onClick={() => setAddingSection(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Section
        </Button>
      )}
    </div>
  );
}
